import React, { useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline'
import whatsNewData from '../../content/whatsNew.json'
import { formatWhatsNewDate } from '../../utils/formatPqrDateTime'
import Button from '../ui/Button'
import Card from '../ui/Card'

type WhatsNewSection = {
  title: string
  items: string[]
}

type WhatsNewData = {
  title: string
  tagline?: string
  frontendVersion?: string
  lastUpdated?: string
  sections: WhatsNewSection[]
}

const data = whatsNewData as unknown as WhatsNewData

type Props = {
  open: boolean
  onClose: () => void
}

export function WhatsNewModal({ open, onClose }: Props) {
  const frontendVersion = useMemo(() => {
    return data.frontendVersion || process.env.REACT_APP_VERSION || '0.1'
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" aria-labelledby="whats-new-title">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden relative z-[10000]">
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-2">
              <SparklesIcon className="w-6 h-6 text-primary-600" />
              <h2 id="whats-new-title" className="text-xl font-bold text-gray-900">{data.title || "What's New"}</h2>
            </div>
            {data.tagline ? <p className="text-sm text-gray-600 mt-1">{data.tagline}</p> : null}
            {data.lastUpdated ? (
              <p className="text-xs text-gray-400 mt-1">
                Last updated: {formatWhatsNewDate(data.lastUpdated)}
              </p>
            ) : null}
          </div>
          <Button variant="secondary" onClick={onClose} aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </Button>
        </div>

        <div className="px-6 pt-6 pb-10 overflow-y-auto max-h-[calc(85vh-88px)] space-y-6">
          <Card title="Current Versions">
            <div className="text-sm text-gray-700 space-y-1">
              <div>
                Frontend: <span className="font-semibold">v{frontendVersion}</span>
              </div>
            </div>
          </Card>

          <div className="space-y-6">
            {Array.isArray(data.sections) &&
              data.sections.map((section) => (
                <div key={section.title}>
                  <h3 className="text-base font-bold text-gray-900">{section.title}</h3>
                  <div className="mt-3 pl-4 border-l-4 border-primary-500">
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700">
                      {(section.items || []).map((item, idx) => (
                        <li key={`${section.title}-${idx}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
